import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { CLOUD_FUNCTIONS } from '../../auth/firebaseApp';
import { InstantiateAttachmentMetaFromJSON } from '../../models/owner-portal/attachmentMeta';
import { CaseGeneral, InstantiateCaseFromJSON } from '../../models/owner-portal/caseGeneral';
import {
  InstantiateListingFromJSON,
  ListingGeneral
} from '../../models/owner-portal/listingGeneral';
import ListingOrderItemGeneral, { InstantiateLOIFromJSON } from '../../models/owner-portal/loi';
import {
  InstantiateReservationFromJSON,
  ReservationGeneral
} from '../../models/owner-portal/reservationGeneral';
import { InstantiateTaskFromJSON, TaskGeneral } from '../../models/owner-portal/taskGeneral';
import {
  InstantiateTimeshareFromJSON,
  TimeshareGeneral,
  TimeshareResort,
  TimeshareBuilding,
  TimeshareViewTypeOption,
  TimeshareComp,
  TimeshareCompBucket,
  TimeshareRegionOption,
  TimeshareRegionsMap,
  TimeshareListingPreview
} from '../../models/owner-portal/timeshareGeneral';
import { formatDateInBrandTZ, scopedRequestDateFormat } from '../../utils/formatTime';
import {
  endpoint_getAttachments,
  endpoint_getCases,
  endpoint_getLOIs,
  endpoint_getListing,
  endpoint_getListings,
  endpoint_getReport,
  endpoint_getReservation,
  endpoint_getReservations,
  endpoint_getStatements,
  endpoint_getTasks,
  endpoint_patchLMI,
  endpoint_postCaseComment,
  endpoint_postReservation,
  endpoint_putReservationCancel,
  endpoint_putReservationUpdate,
  endpoint_patchReservationPaymentMethod,
  // Timeshare endpoints
  endpoint_getTimeshares,
  endpoint_getTimeshare,
  endpoint_getTimeshareComps,
  endpoint_getTimesharePrice,
  endpoint_getTimeshareRegions,
  endpoint_getTimeshareResort,
  endpoint_getTimeshareResorts,
  endpoint_getTimeshareBuildings,
  endpoint_getTimeshareViews,
  endpoint_getTimeshareBedrooms,
  endpoint_getTimeshareUnitTypes,
  endpoint_getTimeshareListing,
  endpoint_getTimeshareListings,
  endpoint_patchTimeshareRentalAmount,
  endpoint_patchTimeshareStatus,
  endpoint_patchTimesharePricingStrategy,
  endpoint_patchTimeshareAcceptRecommendedRate,
  endpoint_postTimeshareRenew,
  endpoint_postTimeshareCreate,
  endpoint_postTimeshareCommissionAgreement,
  endpoint_getTimeshareSingleListing,
  endpoint_getRelatedAccounts,
  endpoint_getOwnerProfile
} from '../../utils/owner-portal/cloudEndpointNames';
import { cloud_makeSalesforceRequest } from '../../utils/owner-portal/cloudFunctions';
import filterCanceledReservations from '../../utils/owner-portal/filterCanceledReservation';
import { OwnerReportType } from '../../utils/owner-portal/ownerReportConstants';
import { brandConfig } from '../../config';

const unusedBaseQuery: BaseQueryFn = (_arg, _api, _extraOptions) => {
  return { data: null };
};

/*
RTK Query caching:

One method is based on 'subscriber count'.
A subscriber is a unique combo of call and params, so doing useGetRTKQListingsQuery('A')
and useGetRTKQListingsQuery('B') makes a subscriber count of two.
When subscriber count hits zero, basically meaning you've navigated away from the page,
the cache timer begins.
So, load page, nav away, wait the timeout, nav back, and you'll get a refetch. If you
nav back before the timout, you'll get cached data.
This can be set per-endpoint.

Another method is refetchOnMountOrArgChange, which can be set to bool (new call every mount),
or a number (new call on mount if X seconds have ellapsed). But that one doesn't (yet)
have a per-endpoint option. We have to pick a global value to share.

We're starting with a one-hour time, for both methods.

We use tags to invalidate data, forcing a refetch. This is done on every mutation. For
example, when you modify a booking (via PUT), that booking is invalidated (meaning removed
from the cache). Then, any components that subscribe to bookings AND provide the booking
tag, will refetch their data automatically.
*/

function validateStandardResultOrThrow(result: any) {
  if (
    typeof result.data !== 'object' ||
    result.data === null ||
    (result.data as any).status !== 200
  ) {
    throw new Error('Invalid result.');
  }
}

//NOTE: This is used for both 'keep unused', after ref count hits zero, and 'force fetch new'
const cacheHoldTime = 60 * 60;

enum EndpointTags {
  Cases = 'Cases',
  Listings = 'Listings',
  Reservations = 'Reservations',
  Reservation = 'Reservation',
  Timeshares = 'Timeshares',
  OwnerProfile = 'OwnerProfile'
}

type OwnerProfile = {
  /** Null when Salesforce has never sent NPS (field omitted/null on payload). */
  npsLastSendDate: Date | null;
  /** Null when the owner has never submitted an NPS response. */
  npsLastSubmitDate: Date | null;
  npsOptOut: boolean;
};

/** Null/empty → null; otherwise require a parseable date string. */
function parseOptionalProfileDate(rawDate: unknown, fieldName: string): Date | null {
  if (rawDate == null || rawDate === '') {
    return null;
  }
  if (typeof rawDate !== 'string') {
    throw new Error(`Invalid ${fieldName} on owner profile.`);
  }
  const date = new Date(Date.parse(rawDate));
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid ${fieldName} on owner profile.`);
  }
  return date;
}

function parseOwnerProfile(resultData: any): OwnerProfile {
  const profile = resultData?.profile;
  if (!profile || typeof profile !== 'object') {
    throw new Error('Failed to get owner profile.');
  }

  const npsOptOut = profile.nps_opt_out;
  if (typeof npsOptOut !== 'boolean') {
    throw new Error('Invalid nps_opt_out on owner profile.');
  }

  return {
    npsLastSendDate: parseOptionalProfileDate(profile.nps_last_send_date, 'nps_last_send_date'),
    npsLastSubmitDate: parseOptionalProfileDate(
      profile.nps_last_submit_date,
      'nps_last_submit_date'
    ),
    npsOptOut
  };
}

const tagArray = Object.values(EndpointTags);

// we have only ONE slices API
export const apiSlice = createApi({
  reducerPath: 'api', // this is the LOCAL path, in memory
  baseQuery: unusedBaseQuery,
  keepUnusedDataFor: cacheHoldTime, // RTKQ's default is one minute
  //TODO: refetchOnMountOrArgChange works globally, but we can't set it per endpoint.
  // SEE: https://github.com/reduxjs/redux-toolkit/issues/2333
  refetchOnMountOrArgChange: cacheHoldTime, // this should mean our cached data is never older than X seconds
  tagTypes: tagArray,
  endpoints(build) {
    return {
      // ------------------------------------------------------
      // ---------   QUERIES (GET calls, basically)   ---------
      // ------------------------------------------------------
      getRTKQCases: build.query({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getCases
          );

          const data = {
            method: 'GET',
            path: '/api/owner/case/list',
            params: { listing_id: args.listingId || '' },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).cases)) {
                throw new Error('Failed to get cases.'); //! This error is public to the user.
              }

              const { cases } = result.data as any;

              const caseModels: CaseGeneral[] = cases.map((caseJSON: any) => {
                const modelInstance = InstantiateCaseFromJSON(caseJSON);
                return modelInstance;
              });

              return { data: caseModels };
            })
            .catch((error) => {
              return { error: error };
            });

          return cloudResult;
        },
        // These can be invalidated to force refresh on update.
        providesTags: (result, error, id) => [{ type: EndpointTags.Cases, id }]
      }),
      getRTKQListingOrderItems: build.query({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getLOIs
          );

          const data = {
            method: 'GET',
            path: '/api/owner/listing/order_items',
            params: { id: args.listingId || '' },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).listing_order_items)) {
                throw new Error('Failed to get listing order items.'); //! This error is public to the user.
              }

              const { listing_order_items } = result.data as any;
              const loiModels: ListingOrderItemGeneral[] = listing_order_items.map(
                (loiJSON: any) => {
                  const loiModel = InstantiateLOIFromJSON(loiJSON);
                  return loiModel;
                }
              );
              // this format is required by RTKQ
              return { data: loiModels };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        }
      }),
      getRTKQListings: build.query({
        queryFn: async (_args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getListings
          );

          const data = {
            method: 'GET',
            path: '/api/owner/listing/list',
            params: {},
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then(async (result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).listings)) {
                throw new Error('Failed to get properties.');
              }

              const { listings } = result.data as any;

              const listingModels: ListingGeneral[] = listings.map((listingJSON: any) => {
                const modelInstance = InstantiateListingFromJSON(listingJSON);
                return modelInstance;
              });

              return { data: listingModels };
            })
            .catch((error) => {
              return { error };
            });

          return cloudResult;
        },
        // These can be invalidated to force refresh on update.
        providesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
      }),
      getRTKQObjectAttachments: build.query({
        queryFn: async (args) => {
          if (!args.objectId) {
            throw new Error('Invalid attempt to get attachments.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getAttachments
          );

          const data = {
            method: 'GET',
            path: '/api/attachment/' + args.objectId,
            params: {},
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);
              if (!Array.isArray((result.data as any).attachments)) {
                throw new Error('Failed to get attachments.'); //! This error is public to the user.
              }
              const models = (result.data as any).attachments.map((json: any) =>
                InstantiateAttachmentMetaFromJSON(json)
              );
              return { data: models };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        }
      }),
      getRTKQOwnerReport: build.query({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getReport
          );

          let endpointUrl = '/api/owner/report/';

          switch (args.type) {
            case OwnerReportType.Booking:
              endpointUrl += 'booking';
              break;
            case OwnerReportType.Occupancy:
              endpointUrl += 'occupancy';
              break;
            case OwnerReportType.Revenue:
              endpointUrl += 'revenue';
              break;
            default:
              throw new Error('Unknown report type.');
          }

          // add the optional params
          const params: Record<string, string> = {};
          if (args.listingId) {
            params.listing_id = args.listingId;
          }
          if (args.startDate) {
            params.start_date = format(args.startDate, scopedRequestDateFormat);
          }
          if (args.endDate) {
            params.end_date = format(args.endDate, scopedRequestDateFormat);
          }

          if (args.type === OwnerReportType.Revenue && !brandConfig.legacyReports) {
            params.use_atx = '1';
          }

          const data = {
            method: 'GET',
            path: endpointUrl,
            params: params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).report) {
                throw new Error('Failed to get report.'); //! This error is public to the user.
              }

              // this format is required by RTKQ
              return { data: (result.data as any).report };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        }
        //NOTE: transformResponse and transformErrorResponse are NOT available w/ queryFn, which we use.
        // SEE: https://redux-toolkit.js.org/rtk-query/api/createApi
      }),
      getRTKQReservations: build.query({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getReservations
          );

          // add the optional params (dates in brand timezone for reservation list)
          const params: Record<string, string> = {};
          if (args.listingId) {
            params.listing_id = args.listingId;
          }
          if (args.startDate) {
            params.start_date = formatDateInBrandTZ(args.startDate);
          }
          if (args.endDate) {
            params.end_date = formatDateInBrandTZ(args.endDate);
          }
          if (args.includePending) {
            params.include_pending =
              args.includePending !== undefined ? args.includePending : false;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/reservation/list',
            params: params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then(async (result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).reservations)) {
                throw new Error('Failed to get reservations.'); //! This error is public to the user.
              }

              const { reservations } = result.data as any;

              const reservationModels: ReservationGeneral[] = reservations.map(
                (reservationJSON: any) => {
                  const modelInstance = InstantiateReservationFromJSON(reservationJSON);
                  return modelInstance;
                }
              );

              const filteredReservationModels = filterCanceledReservations(reservationModels);

              // DEV ONLY: Stress test tool (clone the data)
              // const count = filteredReservationModels.length;
              // for (let i = 0; i < 10; ++i) {
              // 	for (let r = 0; r < count; ++r) {
              // 		filteredReservationModels.push(Object.assign({}, reservationModels[r]));
              // 	}
              // }

              // this format is required by RTKQ
              return { data: filteredReservationModels };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // These can be invalidated to force refresh on update.
        providesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
      }),
      getRTKQSingleListing: build.query({
        queryFn: async (args) => {
          if (!args.listingId) {
            throw new Error('Invalid attempt to get property.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getListing
          );

          const data = {
            method: 'GET',
            path: '/api/owner/listing',
            params: { id: args.listingId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).listing) {
                throw new Error('Failed to get property.'); //! This error is public to the user.
              }

              const listingModel: ListingGeneral = InstantiateListingFromJSON(
                (result.data as any).listing
              );

              //NOTE: We don't filter here, (as we do with get all) because unwanted single reservations should
              //		never be in scope in the first place, and returning null/empty here is very problematic.

              // this format is required by RTKQ
              return { data: listingModel };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // These can be invalidated to force refresh on update.
        providesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
      }),
      getRTKQSingleReservation: build.query({
        queryFn: async (args) => {
          if (!args.reservationId) {
            throw new Error('Invalid attempt to get booking.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getReservation
          );

          const data = {
            method: 'GET',
            path: '/api/owner/reservation',
            params: { id: args.reservationId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).reservation) {
                throw new Error('Failed to get booking.'); //! This error is public to the user.
              }

              const reservationModel: ReservationGeneral = InstantiateReservationFromJSON(
                (result.data as any).reservation
              );

              // this format is required by RTKQ
              return { data: reservationModel };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // These can be invalidated to force refresh on update.
        providesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
      }),
      patchReservationPaymentMethod: build.mutation({
        queryFn: async (args) => {
          if (!args.reservationName || !args.paymentMethodId) {
            throw new Error('Patch requires a reservation name and payment method id.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchReservationPaymentMethod
          );

          const data = {
            method: 'PATCH',
            path: '/api/guest/reservation/' + args.reservationName + '/payment_method',
            params: {
              id: args.paymentMethodId
            }
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              return { data: true };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        // We need to besure to refresh all reservations so that the payment methods in accounts
        // will be updated.
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Reservation, args },
          { type: EndpointTags.Reservation }
        ]
      }),
      getRTKQStatementList: build.query<
        Statement[],
        { startDate?: Date; endDate?: Date } | void
      >({
        queryFn: async (args) => {
          const params: Record<string, string> = {};
          const a = args ?? {};
          if (a.startDate) {
            params.start_date = format(a.startDate, scopedRequestDateFormat);
          }
          if (a.endDate) {
            params.end_date = format(a.endDate, scopedRequestDateFormat);
          }

          const data = {
            method: 'GET',
            path: '/api/owner/statement',
            params,
            body: ''
          };
          const cloudFunction = httpsCallable<
            typeof data,
            { status?: number; statements: Statement[] }
          >(CLOUD_FUNCTIONS, cloud_makeSalesforceRequest + '?' + endpoint_getStatements);
          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!result.data.statements || result.data.status !== 200) {
                throw new Error('Failed to get report.'); //! This error is public to the user.
              }

              return { data: result.data.statements };
            })
            .catch((error) => {
              return { error: error };
            });
          return cloudResult;
        }
      }),
      getRTKQTasks: build.query({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTasks
          );

          // add the optional params
          const params: Record<string, string> = {};

          if (args.listingId) {
            params.listing_id = args.listingId;
          }
          if (args.startDate) {
            params.start_date = format(args.startDate, scopedRequestDateFormat);
          }
          if (args.endDate) {
            params.end_date = format(args.endDate, scopedRequestDateFormat);
          }

          const data = {
            method: 'GET',
            path: '/api/owner/task/list',
            params: params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).tasks)) {
                throw new Error('Failed to get tasks.'); //! This error is public to the user.
              }

              const { tasks } = result.data as any;
              const taskModels: TaskGeneral[] = tasks.map((taskJSON: any) => {
                const taskModel = InstantiateTaskFromJSON(taskJSON);
                return taskModel;
              });
              // this format is required by RTKQ
              return { data: taskModels };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        }
      }),
      // ------------------------------------------------------
      // ---------  TIMESHARE QUERIES   ---------
      // ------------------------------------------------------
      getRTKQTimeshares: build.query({
        queryFn: async (args: { active?: boolean }) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshares
          );

          const params: Record<string, string> = {};
          if (args.active !== undefined) {
            params.active = String(args.active);
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/list',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).timeshares)) {
                throw new Error('Failed to get listings.');
              }

              const { timeshares } = result.data as any;
              const timeshareModels: TimeshareGeneral[] = timeshares.map((json: any) =>
                InstantiateTimeshareFromJSON(json)
              );

              return { data: timeshareModels };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        providesTags: [{ type: EndpointTags.Timeshares, id: 'LIST' }]
      }),
      getRTKQSingleTimeshare: build.query({
        queryFn: async (args: { timeshareId: string }) => {
          if (!args.timeshareId) {
            throw new Error('Invalid attempt to get listing.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshare
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare',
            params: { id: args.timeshareId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to get listing.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        providesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId }
        ]
      }),
      getRTKQTimeshareComps: build.query({
        queryFn: async (args: { listingId: string; checkIn: string; checkOut: string }) => {
          if (!args.listingId || !args.checkIn || !args.checkOut) {
            throw new Error('Listing ID, check-in, and check-out dates are required for comparables.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareComps
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/comps',
            params: {
              listing_id: args.listingId,
              check_in: args.checkIn,
              check_out: args.checkOut
            },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const raw = result.data as {
                comps?: TimeshareComp[];
                comp_buckets?: TimeshareCompBucket[];
              };

              const buckets = Array.isArray(raw.comp_buckets) ? raw.comp_buckets : [];
              const flatComps = Array.isArray(raw.comps)
                ? raw.comps
                : buckets.flatMap((b) => b.comps ?? []);

              if (!Array.isArray(raw.comps) && buckets.length === 0) {
                throw new Error('Failed to get comparables.');
              }

              let marketLowNightlyRate: number | null = null;
              let marketHighNightlyRate: number | null = null;
              for (const b of buckets) {
                if (typeof b.low_nightly_rate === 'number') {
                  marketLowNightlyRate =
                    marketLowNightlyRate == null
                      ? b.low_nightly_rate
                      : Math.min(marketLowNightlyRate, b.low_nightly_rate);
                }
                if (typeof b.high_nightly_rate === 'number') {
                  marketHighNightlyRate =
                    marketHighNightlyRate == null
                      ? b.high_nightly_rate
                      : Math.max(marketHighNightlyRate, b.high_nightly_rate);
                }
              }

              return {
                data: {
                  comps: flatComps,
                  comp_buckets: buckets,
                  marketLowNightlyRate,
                  marketHighNightlyRate
                }
              };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareRegions: build.query<TimeshareRegionOption[], void>({
        queryFn: async () => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareRegions
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/regions',
            params: {},
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const regionOptions = (result.data as any).region_options;
              if (Array.isArray(regionOptions)) {
                return { data: regionOptions as TimeshareRegionOption[] };
              }

              const regions = (result.data as any).regions;
              if (typeof regions !== 'object' || regions === null) {
                throw new Error('Failed to get regions.');
              }
              return {
                data: Object.entries(regions as TimeshareRegionsMap).map(([name, id]) => ({
                  id,
                  name
                }))
              };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareResort: build.query({
        queryFn: async (args: { resortId: string }) => {
          if (!args.resortId) {
            throw new Error('Resort ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareResort
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/resort',
            params: { resort_id: args.resortId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const resort = (result.data as any).resort;
              if (!resort || typeof resort !== 'object') {
                throw new Error('Failed to get resort.');
              }
              return { data: resort as TimeshareResort };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareResorts: build.query({
        queryFn: async (args: { regionId?: string } = {}) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareResorts
          );

          const params: Record<string, string> = {};
          if (args.regionId) {
            params.region_id = args.regionId;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/resorts',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!Array.isArray((result.data as any).resorts)) {
                throw new Error('Failed to get resorts.');
              }

              return { data: (result.data as any).resorts as TimeshareResort[] };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareBuildings: build.query({
        queryFn: async (args: { resortId: string }) => {
          if (!args.resortId) {
            throw new Error('Resort ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareBuildings
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/buildings',
            params: { resort_id: args.resortId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const raw = (result.data as any).buildings;
              if (!Array.isArray(raw)) {
                throw new Error('Failed to get buildings.');
              }
              // Normalize so we always have id/name (backend may send building_id/building_name)
              const data: TimeshareBuilding[] = raw.map((b: any) => ({
                id: b.id ?? b.building_id ?? '',
                name: b.name ?? b.building_name ?? '',
                resort_id: b.resort_id ?? ''
              }));
              return { data };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareViews: build.query({
        queryFn: async (args: { resortId: string; buildingId?: string }) => {
          if (!args.resortId) {
            throw new Error('Resort ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareViews
          );

          const params: Record<string, string> = { resort_id: args.resortId };
          if (args.buildingId) {
            params.building_id = args.buildingId;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/views',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              // Views is a map of view_type -> options[]
              const views = (result.data as any).views as Record<string, TimeshareViewTypeOption[]>;
              return { data: views };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareBedrooms: build.query({
        queryFn: async (args: { resortId: string; buildingId?: string }) => {
          if (!args.resortId) {
            throw new Error('Resort ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareBedrooms
          );

          const params: Record<string, string> = { resort_id: args.resortId };
          if (args.buildingId) {
            params.building_id = args.buildingId;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/bedrooms',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              // bedrooms is a map of label -> value
              const bedrooms = (result.data as any).bedrooms as Record<string, number>;
              return { data: bedrooms };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareUnitTypes: build.query({
        queryFn: async (args: {
          resortId: string;
          buildingId?: string;
          view?: string;
          bedrooms: string;
        }) => {
          if (!args.resortId || !args.bedrooms) {
            throw new Error('Resort ID and bedrooms are required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareUnitTypes
          );

          const params: Record<string, string> = {
            resort_id: args.resortId,
            bedrooms: args.bedrooms
          };
          if (args.buildingId) {
            params.building_id = args.buildingId;
          }
          if (args.view) {
            params.view = args.view;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/unit-types',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const unitTypes = (result.data as any).unit_types as string[];
              return { data: unitTypes };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimeshareListings: build.query({
        queryFn: async (args: {
          resortId: string;
          bedrooms: string;
          buildingId?: string;
        }) => {
          if (!args.resortId || args.bedrooms === undefined || args.bedrooms === '') {
            throw new Error('Resort ID and bedrooms are required for listings.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareListings
          );

          const params: Record<string, string> = {
            resort_id: args.resortId,
            bedrooms: args.bedrooms
          };
          if (args.buildingId) {
            params.building_id = args.buildingId;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/listings',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const raw = (result.data as any).listings;
              if (!Array.isArray(raw)) {
                throw new Error('Failed to get listings.');
              }
              const data: TimeshareListingPreview[] = raw.map((item: any) => ({
                ...item,
                id: null,
                listing_id: item.listing_id ?? '',
                listing_name: item.listing_name ?? '',
                resort_id: item.resort_id ?? '',
                resort_name: item.resort_name ?? '',
                bedrooms: item.bedrooms ?? 0,
                bathrooms: item.bathrooms ?? 0,
                unit_type: item.unit_type ?? '',
                is_float: item.is_float ?? true,
                broker_rate: item.broker_rate ?? 0,
                listing_photo_url: item.listing_photo_url
              })) as TimeshareListingPreview[];
              return { data };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      /**
       * GET /listing?listing_id={listing_id}
       * Returns a single listing preview by ID. Used for renewals to fetch listing data.
       */
      getRTKQTimeshareSingleListing: build.query({
        queryFn: async (args: { listingId: string }) => {
          if (!args.listingId) {
            throw new Error('Listing ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareSingleListing
          );

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/listing',
            params: { listing_id: args.listingId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const item = (result.data as any).listing;
              if (!item || typeof item !== 'object') {
                throw new Error('Failed to get listing.');
              }

              const listing: TimeshareListingPreview = {
                id: null,
                listing_id: item.listing_id ?? '',
                listing_name: item.listing_name ?? '',
                resort_id: item.resort_id ?? '',
                resort_name: item.resort_name ?? '',
                resort_area_id: item.resort_area_id,
                resort_area: item.resort_area,
                resort_city: item.resort_city,
                brand_name: item.brand_name,
                building_id: item.building_id,
                building_name: item.building_name,
                bedrooms: item.bedrooms ?? 0,
                bathrooms: item.bathrooms ?? 0,
                max_guests: item.max_guests,
                view: item.view,
                view_type: item.view_type,
                view_class: item.view_class,
                view_tier: item.view_tier,
                view_orientation: item.view_orientation,
                unit_type: item.unit_type ?? '',
                unit_number: item.unit_number,
                is_float: item.is_float ?? true,
                broker_rate: item.broker_rate ?? 0,
                listing_photo_url: item.listing_photo_url
              };
              return { data: listing };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      /**
       * GET /api/owner/account/related-accounts
       * Returns a list of related accounts for the current user.
       */
      getRTKQRelatedAccounts: build.query({
        queryFn: async () => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getRelatedAccounts
          );

          const data = {
            method: 'GET',
            path: '/api/owner/account/related-accounts',
            params: {},
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const accounts = (result.data as any).accounts;
              if (!Array.isArray(accounts)) {
                throw new Error('Failed to get related accounts.');
              }

              return { data: accounts as Array<{ id: string; name: string }> };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      /**
       * GET /api/owner/profile
       * Returns owner profile fields from Salesforce
       * (`nps_last_send_date`, `nps_last_submit_date`, `nps_opt_out`).
       */
      getRTKQOwnerProfile: build.query({
        queryFn: async () => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getOwnerProfile
          );

          const data = {
            method: 'GET',
            path: '/api/owner/profile',
            params: {},
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);
              return { data: parseOwnerProfile(result.data) };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        providesTags: [EndpointTags.OwnerProfile]
      }),
      getRTKQTimeshareListing: build.query({
        queryFn: async (args: {
          resortId: string;
          buildingId?: string;
          view?: string;
          bedrooms: string;
          unitType: string;
        }) => {
          if (!args.resortId || !args.bedrooms || !args.unitType) {
            throw new Error('Resort ID, bedrooms, and unit type are required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimeshareListing
          );

          const params: Record<string, string> = {
            resort_id: args.resortId,
            bedrooms: args.bedrooms,
            unit_type: args.unitType
          };
          if (args.buildingId) {
            params.building_id = args.buildingId;
          }
          if (args.view) {
            params.view = args.view;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/listing',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const timeshare = (result.data as any)?.timeshare as TimeshareListingPreview | undefined;
              if (!timeshare) {
                return { data: null };
              }
              return { data: { ...timeshare, id: null } as TimeshareListingPreview };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      getRTKQTimesharePrice: build.query({
        queryFn: async (args: {
          listingId: string;
          checkIn: string;
          checkOut: string;
          pricingStrategy?: number;
          nights?: number;
          weekNumber?: string;
          timeshareId?: string;
        }) => {
          if (!args.listingId || !args.checkIn || !args.checkOut) {
            throw new Error('Listing ID, check-in, and check-out are required for price.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_getTimesharePrice
          );

          const params: Record<string, string> = {
            listing_id: args.listingId,
            check_in: args.checkIn,
            check_out: args.checkOut
          };
          if (args.pricingStrategy !== undefined && args.pricingStrategy !== null) {
            params.pricing_strategy = String(args.pricingStrategy);
          }
          if (args.nights !== undefined && args.nights !== null) {
            params.nights = String(args.nights);
          }
          if (args.weekNumber) {
            params.week_number = args.weekNumber;
          }
          if (args.timeshareId) {
            params.timeshare_id = args.timeshareId;
          }

          const data = {
            method: 'GET',
            path: '/api/owner/timeshare/price',
            params,
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const payload = result.data as {
                rental_amount: number | null;
                nightly_rate: number | null;
                broker_rate?: number | null;
                broker_amount?: number | null;
                net_amount?: number | null;
                liability_insurance_fee?: number | null;
                general_excise_broker_rate?: number | null;
                general_excise_broker_amount?: number | null;
              };
              return {
                data: {
                  rentalAmount: payload.rental_amount,
                  nightlyRate: payload.nightly_rate,
                  brokerRate: payload.broker_rate ?? undefined,
                  brokerAmount: payload.broker_amount ?? undefined,
                  netAmount: payload.net_amount ?? undefined,
                  liabilityInsuranceFee: payload.liability_insurance_fee ?? undefined,
                  generalExciseBrokerRate: payload.general_excise_broker_rate ?? undefined,
                  generalExciseBrokerAmount: payload.general_excise_broker_amount ?? undefined
                }
              };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      // ------------------------------------------------------
      // ---------  MUTATIONS (patch/post/put calls)  ---------
      // ------------------------------------------------------
      patchRTKQListingCOI: build.mutation({
        queryFn: async (args) => {
          if (!args.item_id || !args.status) {
            throw new Error('COI update incomplete.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchLMI
          );

          const data = {
            method: 'PATCH',
            path: '/api/owner/listing/coi_status',
            params: args, // this patch endpoint takes the args as params, and no body
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (typeof (result.data as any).management_item !== 'object') {
                throw new Error('Failed to get updated listing item.'); //! This error is public to the user.
              }

              //NOTE: We have a full LMI model here, but we don't need it (yet). RTKQ will reload the listing via invalidate,
              //		which rebuilds with a fresh LMI child model. Still, if needed, use this:
              // const newLMIModel = InstantiateCOIFromJSON(result.data.management_item); << needs to be exported

              // this format is required by RTKQ
              return { data: true };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        invalidatesTags: (result, error, id) => [{ type: EndpointTags.Listings, id }]
      }),
      postRTKQCaseComment: build.mutation({
        queryFn: async (args) => {
          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_postCaseComment
          );

          const data = {
            method: 'POST',
            path: '/api/owner/case/comment',
            params: { id: args.id },
            body: { body: args.body } // this 'body' is the actual comment text entered by the user
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).case_comment) {
                //NOTE: This is redundant. We don't (yet) use the response object.
                throw new Error('Failed to add comment.'); //! This error is public to the user.
              }

              // this format is required by RTKQ
              return { data: true };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        invalidatesTags: (result, error, id) => [{ type: EndpointTags.Cases, id }]
      }),
      postRTKQReservation: build.mutation({
        queryFn: async (args) => {
          if (!args.check_in || !args.check_out || !args.listing || !args.listing.id) {
            throw new Error('New booking form incomplete.');
          }

          //NOTE: We have to carefully check our types here! It's not bool, due to the RHF 'checkboxes' (radios).
          //		They give us string "true"/"false" (and it's not trivial to re-cast that on the way in).
          const boolGuestIsOwnerArg =
            args.guest_is_owner === 'true' || args.guest_is_owner === true;
          const boolOwnerPayFinalClean =
            args.guest_pay_final_cleaning === 'false' || args.guest_pay_final_cleaning === false;
          const boolNoDepartureClean =
            args.no_departure_clean === 'true' || args.no_departure_clean === true;

          const body: any = {
            check_in: args.check_in,
            check_out: args.check_out,
            listing_id: args.listing.id,
            no_departure_clean: boolNoDepartureClean,
            type: boolGuestIsOwnerArg ? 'Owner' : 'Guest of Owner',
            special_requests_detail: args.special_requests_detail
          };

          if (boolGuestIsOwnerArg) {
            body.guest_id = args.externalAccountID;
            body.owner_pays_guest_clean = true;
          } else {
            if (!args.guest_email || !args.guest_first_name || !args.guest_last_name) {
              throw new Error('New booking guest info incomplete.');
            }

            body.guest_email = args.guest_email;
            body.guest_first_name = args.guest_first_name;
            body.guest_last_name = args.guest_last_name;
            if (args.guest_phone) {
              body.guest_phone = args.guest_phone;
            }
            body.owner_pays_guest_clean = boolOwnerPayFinalClean;
          }

          body.stage = 'Confirmed';

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_postReservation
          );

          const data = {
            method: 'POST',
            path: '/api/owner/reservation/booking',
            params: {},
            body: body
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).reservation) {
                throw new Error('Failed to get updated booking.'); //! This error is public to the user.
              }

              // this format is required by RTKQ
              return { data: result.data as Partial<ReservationGeneral> };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
      }),
      putRTKQReservationCancel: build.mutation({
        queryFn: async (args) => {
          if (!args.reservationID) {
            throw new Error('Cancelation info incomplete.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_putReservationCancel
          );

          const data = {
            method: 'PUT',
            path: '/api/owner/reservation/cancel',
            params: { id: args.reservationID },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).reservation) {
                throw new Error('Unable to cancel booking.'); //! This error is public to the user.
              }

              // this format is required by RTKQ
              return { data: true };
            })
            .catch((error) => {
              // this format is required by RTKQ
              return { error: error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
      }),
      putRTKQReservationUpdate: build.mutation({
        queryFn: async (args) => {
          if (!args.id || !args.check_in || !args.check_out) {
            throw new Error('Booking form incomplete.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_putReservationUpdate
          );

          const body = {
            //NOTE: Before the move to single-endpoint, this call was sending up id in the body, and GCF was not removing it.
            //		It's not actually needed by the backend (which takes id as a param), so it's been removed.
            check_in: args.check_in.toISOString(),
            check_out: args.check_out.toISOString()
          };

          const data = {
            method: 'PUT',
            path: '/api/owner/reservation/update',
            params: { id: args.id },
            body: body
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);
              const { reservation, status } = result.data as any;
              if (status !== 200) {
                throw new Error('Failed to get updated booking.'); //! This error is public to the user.
              }
              // this format is required by RTKQ
              return { data: reservation };
            })
            .catch((error) => {
              // this format is required by RTKQ
              console.warn('put reservation update', error);
              return { error: error };
            });

          return cloudResult;
        },
        // This endpoint causes items with these tags to be refetched.
        invalidatesTags: (result, error, id) => [{ type: EndpointTags.Reservations, id }]
      }),
      // ------------------------------------------------------
      // ---------  TIMESHARE MUTATIONS   ---------
      // ------------------------------------------------------
      patchRTKQTimeshareRentalAmount: build.mutation({
        queryFn: async (args: { timeshareId: string; amount: number }) => {
          if (!args.timeshareId || args.amount === undefined) {
            throw new Error('Timeshare ID and amount are required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchTimeshareRentalAmount
          );

          const data = {
            method: 'PATCH',
            path: '/api/owner/timeshare/rental-amount',
            params: { id: args.timeshareId, amount: String(args.amount) },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to update rental amount.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId },
          { type: EndpointTags.Timeshares, id: 'LIST' }
        ]
      }),
      patchRTKQTimeshareStatus: build.mutation({
        queryFn: async (args: { timeshareId: string; status: 'LISTED' | 'UNLISTED' }) => {
          if (!args.timeshareId || !args.status) {
            throw new Error('Timeshare ID and status are required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchTimeshareStatus
          );

          const data = {
            method: 'PATCH',
            path: '/api/owner/timeshare/status',
            params: { id: args.timeshareId, status: args.status },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to update status.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId },
          { type: EndpointTags.Timeshares, id: 'LIST' }
        ]
      }),
      patchRTKQTimesharePricingStrategy: build.mutation({
        queryFn: async (args: { timeshareId: string; pricingStrategy: number }) => {
          if (!args.timeshareId || args.pricingStrategy === undefined) {
            throw new Error('Timeshare ID and pricing strategy are required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchTimesharePricingStrategy
          );

          const data = {
            method: 'PATCH',
            path: '/api/owner/timeshare/pricing-strategy',
            params: { id: args.timeshareId, pricing_strategy: String(args.pricingStrategy) },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to update pricing strategy.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId },
          { type: EndpointTags.Timeshares, id: 'LIST' }
        ]
      }),
      /**
       * PATCH /accept-recommended-rate?id={timeshare_id}
       * Accept the recommended rate for a timeshare. Copies Recommended_Amount__c to Amount__c
       * and clears the recommended amount field.
       */
      patchRTKQTimeshareAcceptRecommendedRate: build.mutation({
        queryFn: async (args: { timeshareId: string }) => {
          if (!args.timeshareId) {
            throw new Error('Timeshare ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_patchTimeshareAcceptRecommendedRate
          );

          const data = {
            method: 'PATCH',
            path: '/api/owner/timeshare/accept-recommended-rate',
            params: { id: args.timeshareId },
            body: ''
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to accept recommended rate.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId },
          { type: EndpointTags.Timeshares, id: 'LIST' }
        ]
      }),
      postRTKQTimeshareRenew: build.mutation({
        queryFn: async (args: { timeshareId: string }) => {
          if (!args.timeshareId) {
            throw new Error('Timeshare ID is required for renewal preview.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_postTimeshareRenew
          );

          const data = {
            method: 'POST',
            path: '/api/owner/timeshare/renew',
            params: {},
            body: { id: args.timeshareId }
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to get renewal preview.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        }
      }),
      postRTKQTimeshareCreate: build.mutation({
        queryFn: async (args: {
          listing_id: string;
          check_in: string;
          check_out: string;
          rental_amount?: number;
          pricing_strategy?: number;
          account_id?: string;
          confirmation_number?: string;
          type?: string;
          fixed_unit: boolean;
          unit_number: string;
        }) => {
          if (!args.listing_id || !args.check_in || !args.check_out) {
            throw new Error('Missing required fields for creating listing.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_postTimeshareCreate
          );

          const body: Record<string, unknown> = {
            listing_id: args.listing_id,
            check_in: args.check_in,
            check_out: args.check_out,
            fixed_unit: args.fixed_unit,
            unit_number: args.unit_number ?? ''
          };
          if (args.rental_amount !== undefined && args.rental_amount !== null) {
            body.rental_amount = args.rental_amount;
          }
          if (args.pricing_strategy !== undefined && args.pricing_strategy !== null) {
            body.pricing_strategy = args.pricing_strategy;
          }
          if (args.account_id != null && args.account_id !== '') {
            body.account_id = args.account_id;
          }
          if (args.confirmation_number != null && args.confirmation_number !== '') {
            body.confirmation_number = args.confirmation_number;
          }
          if (args.type) {
            body.type = args.type;
          }

          const data = {
            method: 'POST',
            path: '/api/owner/timeshare/create',
            params: {},
            body
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              if (!(result.data as any).timeshare) {
                throw new Error('Failed to create listing.');
              }

              const timeshareModel = InstantiateTimeshareFromJSON((result.data as any).timeshare);
              return { data: timeshareModel };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: [{ type: EndpointTags.Timeshares, id: 'LIST' }]
      }),
      postRTKQTimeshareCommissionAgreement: build.mutation({
        queryFn: async (args: { timeshareId: string }) => {
          if (!args.timeshareId) {
            throw new Error('Timeshare ID is required.');
          }

          const cloudFunction = httpsCallable(
            CLOUD_FUNCTIONS,
            cloud_makeSalesforceRequest + '?' + endpoint_postTimeshareCommissionAgreement
          );

          const data = {
            method: 'POST',
            path: '/api/owner/timeshare/commission-agreement',
            params: {},
            body: {
              timeshare_id: args.timeshareId
            }
          };

          const cloudResult = await cloudFunction(data)
            .then((result) => {
              validateStandardResultOrThrow(result);

              const payload = result.data as { envelope_id?: string };
              const envelopeId = payload?.envelope_id;
              if (!envelopeId) {
                throw new Error('Failed to send Commission Agreement.');
              }
              return { data: { envelope_id: envelopeId } };
            })
            .catch((error) => ({ error }));

          return cloudResult;
        },
        invalidatesTags: (result, error, args) => [
          { type: EndpointTags.Timeshares, id: args.timeshareId },
          { type: EndpointTags.Timeshares, id: 'LIST' }
        ]
      })
    };
  }
});

// Export the auto-generated hooks our RTK query endpoint
export const {
  useGetRTKQCasesQuery,
  useGetRTKQListingOrderItemsQuery,
  useGetRTKQListingsQuery,
  useGetRTKQObjectAttachmentsQuery,
  useGetRTKQOwnerReportQuery,
  useGetRTKQReservationsQuery,
  useGetRTKQSingleListingQuery,
  useGetRTKQSingleReservationQuery,
  useGetRTKQStatementListQuery,
  useGetRTKQTasksQuery,
  usePatchRTKQListingCOIMutation,
  usePatchReservationPaymentMethodMutation,
  usePostRTKQCaseCommentMutation,
  usePostRTKQReservationMutation,
  usePutRTKQReservationCancelMutation,
  usePutRTKQReservationUpdateMutation,
  // Timeshare hooks
  useGetRTKQTimesharesQuery,
  useGetRTKQSingleTimeshareQuery,
  useGetRTKQTimeshareCompsQuery,
  useGetRTKQTimeshareRegionsQuery,
  useGetRTKQTimeshareResortQuery,
  useGetRTKQTimeshareResortsQuery,
  useGetRTKQTimeshareBuildingsQuery,
  useGetRTKQTimeshareViewsQuery,
  useGetRTKQTimeshareBedroomsQuery,
  useGetRTKQTimeshareUnitTypesQuery,
  useGetRTKQTimeshareListingQuery,
  useGetRTKQTimeshareListingsQuery,
  useGetRTKQTimeshareSingleListingQuery,
  useGetRTKQTimesharePriceQuery,
  usePatchRTKQTimeshareRentalAmountMutation,
  usePatchRTKQTimeshareStatusMutation,
  usePatchRTKQTimesharePricingStrategyMutation,
  usePatchRTKQTimeshareAcceptRecommendedRateMutation,
  usePostRTKQTimeshareRenewMutation,
  usePostRTKQTimeshareCreateMutation,
  usePostRTKQTimeshareCommissionAgreementMutation,
  useGetRTKQRelatedAccountsQuery,
  useGetRTKQOwnerProfileQuery
} = apiSlice;
